<template lang="html">
  <div id="company-empresa-page">
    <!-- Hero Section -->
    <section class="company-hero position-relative d-flex align-items-center justify-content-center">
      <div
        class="position-absolute w-100 h-100 company-hero-bg"
        :style="{ backgroundImage: 'url(' + heroImageUrl + ')' }"
        aria-hidden="true"
      ></div>
      <div class="position-absolute w-100 h-100 company-hero-overlay" aria-hidden="true"></div>
      <div class="container position-relative text-center">
        <h1 class="company-hero-title text-white font-weight-bold mb-4">
          Sobre <span class="text-white">ECOMéxico</span>
        </h1>
        <p class="company-hero-subtitle text-white mx-auto">
          Más de 25 años transformando la industria de medición y topografía en México
        </p>
      </div>
    </section>

    <!-- About Section -->
    <section class="py-5 bg-white">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-12 col-lg-6 mb-4 mb-lg-0">
            <h2 class="company-section-title mb-4">
              Un poco más <span class="text-danger">sobre nosotros</span>
            </h2>

            <p class="company-intro mb-4">
              Somos una empresa constituida en 1998 con el propósito de ofrecer herramientas y soluciones de medición
              al topógrafo, constructor, geodesta, perito, valuador y a todos aquellos que tengan necesidad de medir,
              calcular, proyectar, nivelar, instalar, montar y construir.
            </p>

            <p class="company-intro mb-4">
              Entendemos las necesidades de nuestros clientes, es por eso que nos comprometemos a ofrecer productos y
              servicios de calidad representando las mejores marcas y productos del mercado.
            </p>

            <div class="company-features">
              <div class="d-flex align-items-start mb-4">
                <div class="company-bullet mr-3"></div>
                <div>
                  <h4 class="font-weight-bold mb-2">Herramientas de Medición</h4>
                  <p class="text-muted mb-0">Soluciones especializadas para topografía, construcción y geodesia</p>
                </div>
              </div>

              <div class="d-flex align-items-start mb-4">
                <div class="company-bullet mr-3"></div>
                <div>
                  <h4 class="font-weight-bold mb-2">Mejores Marcas</h4>
                  <p class="text-muted mb-0">Representamos las marcas más reconocidas y confiables del mercado</p>
                </div>
              </div>

              <div class="d-flex align-items-start">
                <div class="company-bullet mr-3"></div>
                <div>
                  <h4 class="font-weight-bold mb-2">Compromiso con la Calidad</h4>
                  <p class="text-muted mb-0">
                    Productos y servicios que cumplen con los más altos estándares de calidad
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12 col-lg-6">
            <div class="company-image-wrap position-relative">
              <img
                src="public/images/pages/company/leica-surveyor-field.jpg"
                alt="Topógrafa profesional usando equipo Leica GS05 en campo"
                class="img-fluid rounded shadow"
                @error="$event.target.src='public/images/shared/placeholder.svg'"
              />
              <div class="company-image-overlay"></div>
            </div>
          </div>
        </div>

        <!-- Achievements -->
        <div class="row mt-5 company-achievements">
          <div
            v-for="(achievement, index) in achievements"
            :key="index"
            class="col-6 col-lg-3 mb-4 mb-lg-0"
          >
            <div class="card border-0 shadow h-100 text-center company-achievement-card">
              <div class="card-body p-4">
                <div class="company-achievement-icon mx-auto mb-3">
                  <i :class="['fa-solid', achievement.icon, 'fa-2x', 'text-danger']"></i>
                </div>
                <div class="h4 font-weight-bold mb-2">{{ achievement.number }}</div>
                <div class="h6 font-weight-bold text-danger mb-2">{{ achievement.label }}</div>
                <p class="small text-muted mb-0">{{ achievement.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Values Section -->
    <section class="py-5 bg-light">
      <div class="container">
        <div class="text-center mb-5">
          <h2 class="company-section-title mb-4">
            Nuestros <span class="text-danger">Valores</span>
          </h2>
          <p class="company-intro mx-auto">
            Los principios que guían nuestro trabajo diario y definen nuestra identidad como empresa
          </p>
        </div>

        <div class="row">
          <div
            v-for="(value, index) in values"
            :key="index"
            class="col-12 col-md-6 mb-4"
          >
            <div class="card border-0 shadow h-100 company-value-card">
              <div class="card-body p-4">
                <div class="d-flex align-items-start">
                  <div class="company-value-icon mr-4 flex-shrink-0">
                    <i :class="['fa-solid', value.icon, 'fa-lg', 'text-danger']"></i>
                  </div>
                  <div>
                    <h3 class="h5 font-weight-bold mb-3">{{ value.title }}</h3>
                    <p class="text-muted mb-0">{{ value.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- History Section -->
    <section class="py-5 bg-white">
      <div class="container">
        <div class="text-center mb-5">
          <h2 class="company-section-title mb-4">
            Nuestra <span class="text-danger">Historia</span>
          </h2>
          <p class="company-intro mx-auto">
            Un recorrido de más de dos décadas construyendo el futuro de la medición en México
          </p>
        </div>

        <div class="company-history">
          <div
            v-for="(item, index) in history"
            :key="index"
            class="d-flex align-items-start mb-4"
          >
            <div class="company-history-year flex-shrink-0 text-right mr-3">
              <div class="h4 font-weight-bold text-danger">{{ item.year }}</div>
            </div>
            <div class="flex-grow-1 bg-light p-4 rounded">
              <h3 class="h5 font-weight-bold mb-2">{{ item.title }}</h3>
              <p class="text-muted mb-0">{{ item.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'companyEmpresaPage',
  data() {
    return {
      heroImageUrl: 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/image-g7K2hWXebps4bGG4SFBj19S3zVQZtm.png',
      achievements: [
        {
          icon: 'fa-award',
          number: '25+',
          label: 'Años de Experiencia',
          description: 'Desde 1998 ofreciendo soluciones de medición',
        },
        {
          icon: 'fa-users',
          number: '1000+',
          label: 'Clientes Satisfechos',
          description: 'Topógrafos, constructores y profesionales',
        },
        {
          icon: 'fa-globe',
          number: 'Nacional',
          label: 'Cobertura',
          description: 'Servicio en toda la República Mexicana',
        },
        {
          icon: 'fa-bolt',
          number: 'Calidad',
          label: 'Garantizada',
          description: 'Representando las mejores marcas del mercado',
        },
      ],
      values: [
        {
          icon: 'fa-bullseye',
          title: 'Misión',
          description:
            'Proporcionar soluciones tecnológicas innovadoras de medición y geomática que impulsen la productividad y precisión de nuestros clientes en México.',
        },
        {
          icon: 'fa-heart',
          title: 'Visión',
          description:
            'Ser la empresa líder en México en distribución de equipos de medición y tecnología geoespacial, reconocida por la calidad de nuestros productos y servicio al cliente.',
        },
        {
          icon: 'fa-shield-halved',
          title: 'Compromiso',
          description:
            'Nos comprometemos a ofrecer productos de la más alta calidad, respaldo técnico experto y un servicio personalizado que supere las expectativas de nuestros clientes.',
        },
        {
          icon: 'fa-lightbulb',
          title: 'Innovación',
          description:
            'Mantenemos una constante actualización tecnológica para ofrecer las soluciones más avanzadas del mercado, adaptándonos a las necesidades cambiantes de la industria.',
        },
      ],
      history: [
        {
          year: '1998',
          title: 'Fundación',
          description:
            'ECOMéxico nace con la visión de ofrecer soluciones de medición de calidad al mercado mexicano, iniciando como distribuidor de equipos topográficos.',
        },
        {
          year: '2005',
          title: 'Expansión Nacional',
          description:
            'Expandimos nuestra cobertura a toda la República Mexicana, estableciendo una red de distribuidores y soporte técnico en las principales ciudades del país.',
        },
        {
          year: '2015',
          title: 'Tecnología de Drones',
          description:
            'Incorporamos tecnología de drones profesionales DJI a nuestro catálogo, convirtiéndonos en distribuidores oficiales y líderes en soluciones aéreas para topografía y construcción.',
        },
        {
          year: '2025',
          title: 'Innovación Continua',
          description:
            'Continuamos innovando y expandiendo nuestro portafolio con las tecnologías más avanzadas del mercado, manteniendo nuestro compromiso con la excelencia y el servicio al cliente.',
        },
      ],
    };
  },
};
</script>
